import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import {
  Box,
  Unstable_Grid2 as Grid,
  Button,
  Link as MuiLink,
} from '@mui/material'
import { substanceRoute, productDetailRoute, useRouter } from '@src/routes'
import { CatalogType } from '@src/types/graphql-types'
import {
  SubstanceFieldsFragment,
  SubstanceProductFieldsFragment,
} from '@src/queries/ProductSearchQuery.generated'
import messages from '@utils/messages'
import {
  SearchFocusType,
  ProductSearchType,
  determineQueryFocus,
  determineCatalogType,
  getValidSearchQuery,
} from '@utils/searchUtils'
import { Link } from '@src/components/Link'
import CatalogImage from '@src/components/CatalogImage'
import QuickViewSubstanceModal from '../QuickViewSubstanceModal'
import HandleMarkup from '../HandleMarkup'
import {
  SubstanceTitleSegment,
  SubstanceInfoSegment,
} from '../SubstanceDetailSegment'

const useStyles = makeStyles()((theme) => {
  return {
    cardColor: {
      backgroundColor: theme.palette.common.white,
      [theme.breakpoints.down('sm')]: {
        background: theme.palette.background.grey,
        padding: 0,
      },
    },
    substanceNameWrapper: {
      paddingLeft: 30,
      [theme.breakpoints.down('sm')]: {
        paddingTop: 0,
        paddingLeft: 0,
        pointerEvents: 'none',
      },
    },
    substanceName: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(16),
      lineHeight: '1.43rem',
      wordBreak: 'break-all',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      [theme.breakpoints.down('sm')]: {
        color: theme.palette.common.black,
      },
    },
    link: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    button: {
      bottom: 5,
      fontSize: theme.typography.pxToRem(16),
      opacity: 0,
      position: 'absolute',
      transition: 'opacity .2s ease-in-out',
      width: '160px',
      [theme.breakpoints.down('lg')]: {
        display: 'none',
      },
    },
    imgContainer: {
      paddingTop: '0 !important',
    },
    imgButtonGroup: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    imgWrapper: {
      height: theme.typography.pxToRem(185),
      width: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& img': {
        maxWidth: '85%',
        maxHeight: '85%',
        height: 'auto',
        width: 'auto',
      },
      [theme.breakpoints.down('lg')]: {
        height: theme.typography.pxToRem(120),
        '& img': {
          maxWidth: '65%',
          maxHeight: '65%',
          border: `1px solid ${theme.palette.grey[600]}`,
        },
      },
    },
    substanceGridWrapper: {
      [theme.breakpoints.down('lg')]: {
        paddingTop: 30,
      },
    },
    productDetails: {
      fontSize: theme.typography.pxToRem(12),
      minHeight: 60,
      paddingLeft: 30,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
      },
    },
    percentage: {
      fontWeight: theme.typography.fontWeightBold,
      marginRight: theme.spacing(1),
    },
    truncate: {
      width: 140,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    similarity: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(1),
    },
  }
})

export const substanceSimilarityPct = (
  products?: SubstanceProductFieldsFragment[]
) => {
  if (!products || !products.length) return null
  const firstProduct = products[0]
  if (products.length === 1) {
    return firstProduct.similarity
  } else {
    /**
     * if substance has more than 1 product, only display similarity pct on substance if all are the same
     * Otherwise we have conflicting values and cannot determine which to display to user (we can only display 1)
     */
    const restOfProducts = products.slice(1, products.length)
    const allTheSameSimilarity = restOfProducts.every(
      (product) => product.similarity === firstProduct.similarity
    )
    return allTheSameSimilarity ? firstProduct.similarity : null
  }
}
// optional override prop if focus is set by a no results product search first
interface SubstanceCardProps {
  colorIndex?: number
  catalogOverride?: CatalogType
  substance: SubstanceFieldsFragment
}

export const SubstanceCard: React.FC<SubstanceCardProps> = ({
  substance,
  catalogOverride = null,
  colorIndex = 0,
}) => {
  const { classes } = useStyles()

  const products: any = substance.products
    ? [...substance.products]
    : [substance]

  const substanceWithProducts = {
    ...substance,
    products,
  }

  const { casNumber, molecularWeight, linearFormula, name, id, images } =
    substanceWithProducts

  const [modalOpen, setModalOpen] = useState(false)
  const router = useRouter()
  const routerCatalogType = router.query.catalogType
  const validSearchQuery = getValidSearchQuery({
    focus: router.query.focus,
    catalogType: determineCatalogType(routerCatalogType),
  })
  // check for catalogType in query first - WIBF-1498
  const catalogType =
    catalogOverride ??
    determineCatalogType(validSearchQuery.catalogType || validSearchQuery.focus)
  const renderSubstanceName = () => {
    const defaultName = (
      <title className={classes.substanceName}>
        <span title={name}>
          <HandleMarkup value={name} />
        </span>
      </title>
    )
    return products?.length === 1 ? (
      <Link
        {...productDetailRoute.index(
          products[0].brand.key,
          products[0].productKey,
          { catalog: catalogType }
        )}
        passHref
      >
        <MuiLink>{defaultName}</MuiLink>
      </Link>
    ) : (
      <Link {...substanceRoute.index(id, catalogType)} passHref>
        <MuiLink>{defaultName}</MuiLink>
      </Link>
    )
  }
  const substanceSimliarityValue =
    validSearchQuery.focus === SearchFocusType.StructureSearch
      ? substanceSimilarityPct(products)
      : null

  return (
    <Grid
      container
      alignItems="flex-start"
      data-testid="srp-substance-group-grid"
      id="srp-substance-group-grid"
      className={colorIndex % 2 === 0 ? classes.cardColor : ''}
    >
      <Grid xs={4} sm={12} className={classes.imgContainer}>
        <div className={classes.imgButtonGroup}>
          <div className={classes.imgWrapper}>
            <CatalogImage
              image={images[0]}
              altTextFallback={name}
              width="270"
              height="185"
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={() => setModalOpen(true)}
          >
            <FormattedMessage {...messages.QUICK_VIEW} />
          </Button>
        </div>
      </Grid>
      <Grid xs={8} sm={12} container className={classes.substanceGridWrapper}>
        <div className={classes.substanceNameWrapper}>
          {renderSubstanceName()}
        </div>

        <Grid container className={classes.productDetails}>
          <Grid container direction="column" sm={12} spacing={1}>
            {casNumber ? (
              <Grid>
                <Box display="flex" flex-direction="row">
                  <SubstanceTitleSegment message={messages.CAS_NUMBER_ABBREV} />
                  <SubstanceInfoSegment
                    searchFocusType={determineQueryFocus(
                      validSearchQuery.focus
                    )}
                    searchValue={casNumber}
                    productSearchType={ProductSearchType.CasNumber}
                    classes={classes.link}
                  />
                </Box>
              </Grid>
            ) : null}
            {molecularWeight ? (
              <Grid>
                <Box display="flex" flex-direction="row">
                  <SubstanceTitleSegment
                    message={messages.MOLECULAR_WEIGHT_ABBREV}
                  />
                  <HandleMarkup
                    value={molecularWeight}
                    className={classes.truncate}
                  />
                </Box>
              </Grid>
            ) : null}
            {linearFormula ? (
              <Grid>
                <Box display="flex" flex-direction="row">
                  <SubstanceTitleSegment
                    message={messages.MOLECULAR_FORMULA_ABBREV}
                  />
                  <HandleMarkup
                    value={linearFormula}
                    className={classes.truncate}
                  />
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        {substanceSimliarityValue ? (
          <Grid container sm={12} className={classes.similarity}>
            <>
              <Grid>
                <span
                  className={classes.percentage}
                >{`${substanceSimliarityValue}% `}</span>
              </Grid>
              <Grid>
                <FormattedMessage {...messages.SIMILARITY} />
              </Grid>
            </>
          </Grid>
        ) : null}
      </Grid>
      <QuickViewSubstanceModal
        catalogOverride={catalogType}
        catalogType={catalogType}
        open={modalOpen}
        setModalOpen={setModalOpen}
        substance={substance}
        substanceName={renderSubstanceName()}
      />
    </Grid>
  )
}

export default SubstanceCard
